"use client";
import { useState, useEffect } from 'react';
import AppLayout from "@/components/layout/AppLayout";
import Link from 'next/link';
import Image from 'next/image';
import { ContactPageSchema } from '@/components/seo/SchemaMarkup';
import SEOLinks from '@/components/seo/SEOLinks';
import { trackFormSubmission, trackDemoRequest } from '@/lib/analytics';
import siteConfig from '@/config/siteConfig';

// Interface for form data
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  companySize: string;
  industry: string;
  interests: string;
  message: string;
  privacy: boolean;
  botField: string;
}

// Interface for form message
interface FormMessage {
  text: string;
  type: 'success' | 'error' | null;
}

// Interface for form element
interface DemoFormElement extends HTMLFormElement {
  firstName: HTMLInputElement;
  lastName: HTMLInputElement;
  email: HTMLInputElement;
  phone: HTMLInputElement;
  company: HTMLInputElement;
  jobTitle: HTMLInputElement;
  companySize: HTMLSelectElement;
  industry: HTMLSelectElement;
  interests: HTMLSelectElement;
  message: HTMLTextAreaElement;
  privacy: HTMLInputElement;
  botField: HTMLInputElement;
}

// Debounce function type
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface DebounceFunction {
  (form: DemoFormElement): Promise<void>;
}

// Custom debounce function
const debounce = (func: (form: DemoFormElement) => Promise<void>, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (form: DemoFormElement) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(form), wait);
  };
};

export default function ScheduleDemoPage() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    companySize: '',
    industry: '',
    interests: '',
    message: '',
    privacy: false,
    botField: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [exitIntentShown, setExitIntentShown] = useState(false);
  const [message, setMessage] = useState<FormMessage>({
    text: '',
    type: null
  });

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' && 'checked' in e.target ? e.target.checked : false;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Async function to handle form submission
  const submitForm = async (form: DemoFormElement) => {
    const botField = form.botField.value;
    const email = form.email.value;

    // Honeypot check
    if (botField) {
      setMessage({ text: 'Submission blocked. Please try again.', type: 'error' });
      return;
    }

    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setMessage({ text: 'Please enter a valid email address.', type: 'error' });
      return;
    }

    setIsSubmitting(true);
    setMessage({ text: '', type: null });

    try {
      const response = await fetch('/api/forms/demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setMessage({ text: 'Demo request submitted successfully!', type: 'success' });
        setShowConfirmation(true);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          jobTitle: '',
          companySize: '',
          industry: '',
          interests: '',
          message: '',
          privacy: false,
          botField: ''
        });
        trackDemoRequest();
        trackFormSubmission('Demo Request', true);
      } else {
        const errorData = await response.json();
        setMessage({ text: errorData.error || 'Submission failed. Please try again.', type: 'error' });
        trackFormSubmission('Demo Request', false);
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setMessage({ text: 'An error occurred. Please try again later.', type: 'error' });
      trackFormSubmission('Demo Request', false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Debounce the submission (500ms delay)
  const debouncedSubmit = debounce((form: DemoFormElement) => submitForm(form), 500);

  // Handle form submission event
  const handleSubmit = (e: React.FormEvent<DemoFormElement>) => {
    e.preventDefault();
    const form = e.target as DemoFormElement;
    debouncedSubmit(form);
  };

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !exitIntentShown && !showConfirmation) {
        setExitIntentShown(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [exitIntentShown, showConfirmation]);

  // Close confirmation popup
  const closeConfirmation = () => {
    setShowConfirmation(false);
  };

  // Close exit intent popup
  const closeExitIntent = () => {
    setExitIntentShown(false);
  };

  return (
    <>
      <meta name="author" content={siteConfig.authors} />
      <meta property="article:published_time" content={siteConfig.publishedTime} />
      <meta property="fb:app_id" content={siteConfig.FacebookAppID} />
      <ContactPageSchema />
      <SEOLinks customPath="/contact/schedule-demo" />
      <AppLayout>
        {/* Hero Section */}
        <section className="gradient-bg text-white">
          <div className="container py-16 md:py-24">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h1 className="text-white mb-6">
                  Schedule a Demo
                </h1>
                <p className="text-lg mb-8 text-gray-100">
                  See our Commerce Cloud platform in action with a personalized demo tailored to your business needs.
                </p>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/10 rounded-lg h-80 flex items-center justify-center">
                  <Image
                    src="/images/hero/demo-visualization.jpg"
                    alt="Demo Visualization"
                    width={600}
                    height={320}
                    priority={true}
                    fetchPriority="high"
                    loading="eager"
                    className="rounded-lg"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Demo Form Section */}
        <section className="section bg-white">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="mb-4">Request Your Personalized Demo</h2>
                <p className="mb-8 text-gray-800">
                  Fill out the form below to schedule a personalized demo of our Commerce Cloud platform. Our team will contact you to arrange a time that works for you.
                </p>
                
                {/* Form Status Messages with ARIA Live Region */}
                <div 
                  role="status" 
                  aria-live="polite" 
                  aria-atomic="true"
                  className="mb-4"
                >
                  {message.text && (
                    <div 
                      className={`p-4 rounded-md ${
                        message.type === 'success' 
                          ? 'bg-green-50 text-green-800 border border-green-200' 
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}
                      role="alert"
                    >
                      {message.text}
                    </div>
                  )}
                </div>
                
                <form 
                  className="space-y-6" 
                  onSubmit={handleSubmit}
                  aria-busy={isSubmitting}
                  noValidate
                >
                  {/* Honeypot field - hidden from users but visible to bots */}
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <label htmlFor="botField">Leave this field empty</label>
                    <input
                      type="text"
                      name="botField"
                      id="botField"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.botField}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-900 mb-1">
                        First Name*
                      </label>
                      <input 
                        type="text" 
                        id="firstName" 
                        name="firstName" 
                        value={formData.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                        required 
                        aria-required="true"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-900 mb-1">
                        Last Name*
                      </label>
                      <input 
                        type="text" 
                        id="lastName" 
                        name="lastName" 
                        value={formData.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                        required 
                        aria-required="true"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1">
                      Business Email*
                    </label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email" 
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      required 
                      aria-required="true"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-1">
                      Phone Number*
                    </label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      required 
                      aria-required="true"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-1">
                      Company Name*
                    </label>
                    <input 
                      type="text" 
                      id="company" 
                      name="company" 
                      value={formData.company}
                      onChange={handleChange}
                      autoComplete="organization"
                      required 
                      aria-required="true"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-900 mb-1">
                      Job Title*
                    </label>
                    <input 
                      type="text" 
                      id="jobTitle" 
                      name="jobTitle" 
                      value={formData.jobTitle}
                      onChange={handleChange}
                      autoComplete="organization-title"
                      required 
                      aria-required="true"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="companySize" className="block text-sm font-medium text-gray-900 mb-1">
                      Company Size
                    </label>
                    <select 
                      id="companySize" 
                      name="companySize" 
                      value={formData.companySize}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="501-1000">501-1000 employees</option>
                      <option value="1000+">1000+ employees</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="industry" className="block text-sm font-medium text-gray-900 mb-1">
                      Industry
                    </label>
                    <select 
                      id="industry" 
                      name="industry" 
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors"
                    >
                      <option value="">Select industry</option>
                      <option value="retail-consumer">Retail & Consumer</option>
                      <option value="sports-entertainment">Sports & Entertainment</option>
                      <option value="food-beverage">Food & Beverage</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="interests" className="block text-sm font-medium text-gray-900 mb-1">
                      What are you most interested in?
                    </label>
                    <select 
                      id="interests" 
                      name="interests" 
                      value={formData.interests}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors"
                    >
                      <option value="">Select your primary interest</option>
                      <option value="e-commerce">E-commerce Module</option>
                      <option value="marketplace">Marketplace Module</option>
                      <option value="analytics">Analytics & Insights</option>
                      <option value="loyalty">Loyalty Programs</option>
                      <option value="all">All of the above</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-1">
                      Additional Information
                    </label>
                    <textarea 
                      id="message" 
                      name="message" 
                      rows={4} 
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00A4BD] focus:border-[#00A4BD] text-gray-900 transition-colors" 
                      placeholder="Tell us about your specific needs or questions"
                    ></textarea>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input 
                        id="privacy" 
                        name="privacy" 
                        type="checkbox" 
                        checked={formData.privacy}
                        onChange={handleChange}
                        required 
                        aria-required="true"
                        className="h-4 w-4 text-[#00A4BD] focus:ring-2 focus:ring-[#00A4BD] border-gray-300 rounded transition-colors" 
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="privacy" className="text-gray-800">
                        I agree to the <Link href="/privacy-policy" className="text-[#00A4BD] hover:underline">Privacy Policy</Link> and consent to Perxify processing my data for the purpose of contacting me about a demo.
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <button 
                      type="submit" 
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed transition-opacity focus:ring-2 focus:ring-offset-2 focus:ring-[#00A4BD]"
                      disabled={isSubmitting}
                      aria-busy={isSubmitting}
                      aria-describedby={isSubmitting ? "submit-status" : undefined}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="inline-flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting...
                          </span>
                          <span className="sr-only" id="submit-status">Form is being submitted, please wait</span>
                        </>
                      ) : (
                        'Schedule Demo'
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              <div>
                <div className="bg-gray-50 p-8 rounded-lg">
                  <h3 className="text-xl mb-6 text-gray-900">What to Expect</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="text-[#00A4BD] font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1 text-gray-900">Submit Your Request</h4>
                        <p className="text-gray-700">
                          Fill out the form with your information and specific areas of interest.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="text-[#00A4BD] font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1 text-gray-900">Confirmation</h4>
                        <p className="text-gray-700">
                          You'll receive a confirmation email with details about your demo request.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="text-[#00A4BD] font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1 text-gray-900">Scheduling</h4>
                        <p className="text-gray-700">
                          Our team will contact you within 24 hours to schedule a time that works for you.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="text-[#00A4BD] font-bold">4</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1 text-gray-900">Personalized Demo</h4>
                        <p className="text-gray-700">
                          Enjoy a tailored demonstration of our Commerce Cloud platform focused on your specific needs.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-[#E6F7F9] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                        <span className="text-[#00A4BD] font-bold">5</span>
                      </div>
                      <div>
                        <h4 className="font-medium mb-1 text-gray-900">Follow-Up</h4>
                        <p className="text-gray-700">
                          After the demo, we'll provide additional resources and answer any questions you may have.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-white border border-gray-200 rounded-lg p-8">
                  <h3 className="text-xl mb-4 text-gray-900">Why Choose Perxify?</h3>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-[#00A4BD] mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-800">AI-native commerce cloud for innovative experiences</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-[#00A4BD] mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-800">Comprehensive platform with modular components</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-[#00A4BD] mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-800">Flexible solutions for retailers, marketplaces, and brands</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-[#00A4BD] mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-800">Powerful analytics and insights for data-driven decisions</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-[#00A4BD] mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-800">Scalable infrastructure that grows with your business</span>
                    </li>
                  </ul>
                  
                  <div className="mt-6">
                    <Link href="/case-studies" className="text-[#00A4BD] font-medium hover:text-[#008CA1]">
                      View our case studies →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Confirmation Popup */}
        {showConfirmation && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
            role="dialog" 
            aria-modal="true"
            aria-labelledby="confirmation-title"
            aria-describedby="confirmation-description"
          >
            <div className="bg-white rounded-lg p-8 max-w-md w-full">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#E6F7F9] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#00A4BD]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 id="confirmation-title" className="text-2xl font-bold mb-2 text-gray-900">Thank you for your interest in Perxify!</h3>
                <p id="confirmation-description" className="text-gray-700">
                  Our team will contact you within 1-3 days to schedule your personalized demo.
                </p>
              </div>
              <div className="text-center">
                <button
                  onClick={closeConfirmation}
                  className="btn-primary"
                  autoFocus
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Exit Intent Popup */}
        {exitIntentShown && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
            role="dialog" 
            aria-modal="true"
            aria-labelledby="exit-intent-title"
            aria-describedby="exit-intent-description"
          >
            <div className="bg-white rounded-lg p-8 max-w-md w-full relative">
              <button
                onClick={closeExitIntent}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:ring-2 focus:ring-[#00A4BD] rounded"
                aria-label="Close dialog"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="text-center mb-6">
                <h3 id="exit-intent-title" className="text-2xl font-bold mb-2 text-gray-900">Wait! Don't Miss Out</h3>
                <p id="exit-intent-description" className="text-gray-700 mb-4">
                  See how Perxify can transform your commerce experience with our AI-powered platform for the Agentic Web.
                </p>
                <p className="text-gray-700">
                  Schedule a demo today and discover how our solution can help you drive growth and engagement.
                </p>
              </div>
              <div className="text-center">
                <button
                  onClick={() => {
                    closeExitIntent();
                    window.scrollTo({
                      top: document.querySelector('form')?.offsetTop ?? 0 - 100,
                      behavior: 'smooth'
                    });
                  }}
                  className="btn-primary"
                  autoFocus
                >
                  Schedule My Demo
                </button>
              </div>
            </div>
          </div>
        )}
      </AppLayout>
    </>
  );
}